import template from './sellops-analytics.html.twig';

const { Component } = Shopware;

function today() {
    return new Date().toISOString().split('T')[0];
}

function daysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split('T')[0];
}

Component.register('sellops-analytics', {
    template,

    inject: ['loginService', 'sellopsUpsellApiService'],

    data() {
        return {
            isLoading: false,
            quickRange: '30',
            dateFrom: daysAgo(30),
            dateTo: today(),
            stats: {
                totalImpressions: 0,
                totalOrders: 0,
                conversionRate: 0,
                totalRevenue: 0,
                currencyIso: '',
                daily: [],
                dailyTotal: 0,
                page: 1,
                limit: 10,
            },
            currentPage:    1,
            itemsPerPage:   10,
            presetPerPage:  '10',           // matches the default itemsPerPage
            customPerPage:  '',             // empty until merchant picks "Custom"
            perPageMin:     1,
            perPageMax:     500,
        };
    },

    created() {
        this.loadAnalytics();
    },

    computed: {
        rangeStart() {
            const total = this.stats?.dailyTotal || 0;
            if (total === 0) return 0;
            return ((this.currentPage - 1) * this.itemsPerPage) + 1;
        },
        rangeEnd() {
            const total = this.stats?.dailyTotal || 0;
            const end   = this.currentPage * this.itemsPerPage;
            return Math.min(end, total);
        },

        columns() {
            return [
                { property: 'date',        label: this.$tc('sellops.analytics.colDate'),        sortable: true },
                { property: 'impressions', label: this.$tc('sellops.analytics.colImpressions'), sortable: true },
                { property: 'orders',      label: this.$tc('sellops.analytics.colOrders'),       sortable: true },
                { property: 'revenue',     label: this.$tc('sellops.analytics.colRevenue'),      sortable: true },
            ];
        },
    },

    methods: {
        onPresetChange(event) {
            const value = event.target.value;
            this.quickRange = value;

            if (value === 'custom') {
                return;
            }

            this.dateFrom    = daysAgo(parseInt(value, 10));
            this.dateTo      = today();
            this.currentPage = 1; // a new date range means the old page numbers no longer make sense
            this.loadAnalytics();
        },

        onDateFromChange(event) {
            this.dateFrom = event.target.value;
        },

        onDateToChange(event) {
            this.dateTo = event.target.value;
        },

        onPerPagePresetChange(event) {
            this.presetPerPage = event.target.value;
            if (this.presetPerPage !== 'custom') {
                this.itemsPerPage = parseInt(this.presetPerPage, 10) || 10;
                this.currentPage  = 1;
                this.loadAnalytics();
            }
        },

        onCustomPerPageChange(event) {
            const raw = String(event.target.value || '').trim();
            const n   = parseInt(raw, 10);
            if (!Number.isFinite(n) || n < this.perPageMin) {
                this.itemsPerPage  = this.perPageMin;
                this.customPerPage = String(this.perPageMin);
            } else if (n > this.perPageMax) {
                this.itemsPerPage  = this.perPageMax;
                this.customPerPage = String(this.perPageMax);
            } else {
                this.itemsPerPage  = n;
                this.customPerPage = String(n);
            }
            this.currentPage = 1;
            this.loadAnalytics();
        },

        onPageChange(newPage) {
            const p = (newPage && typeof newPage === 'object') ? newPage.page : newPage;
            const parsed = parseInt(p, 10);
            if (!Number.isFinite(parsed) || parsed < 1) return;
            this.currentPage = parsed;
            this.loadAnalytics();
        },

        async loadAnalytics() {
            if (!this.dateFrom || !this.dateTo) return;

            this.isLoading = true;
            try {
                this.stats = await this.sellopsUpsellApiService.getAnalytics({
                    dateFrom: this.dateFrom,
                    dateTo:   this.dateTo,
                    page:     this.currentPage,
                    limit:    this.itemsPerPage,
                });

                if (this.stats && typeof this.stats.page === 'number')  this.currentPage  = this.stats.page;
                if (this.stats && typeof this.stats.limit === 'number') this.itemsPerPage = this.stats.limit;
            } catch { /* ignore */ } finally {
                this.isLoading = false;
            }
        },

        adminCurrency() {
            try {
                const iso = /^[A-Z]{3}$/;
                const api = this.stats?.currencyIso;
                if (typeof api === 'string' && iso.test(api)) return api;

                const c = Shopware?.Context?.app?.systemCurrencyISOCode;
                return (typeof c === 'string' && iso.test(c)) ? c : 'EUR';
            } catch{
                return 'EUR';
            }
        },
        adminLocale() {
            const candidates = [];
            try { candidates.push(Shopware?.State?.get?.('session')?.currentLocale); } catch { /* ignore */ }
            try { candidates.push(Shopware?.Context?.app?.fallbackLocale);            } catch { /* ignore */ }
            try { candidates.push(navigator.language);                                } catch { /* ignore */ }

            const looksLikeLocale = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/i;
            for (const c of candidates) {
                if (typeof c === 'string' && looksLikeLocale.test(c)) {
                    return c;
                }
            }
            return 'en-GB';
        },

        formatNumber(val) {
            try {
                return new Intl.NumberFormat(this.adminLocale()).format(val ?? 0);
            } catch{
                return String(val ?? 0);
            }
        },

        formatCurrency(val) {
            const n = Number(val ?? 0);
            try {
                return new Intl.NumberFormat(this.adminLocale(), {
                    style:                 'currency',
                    currency:              this.adminCurrency(),
                    minimumFractionDigits: 2,
                }).format(n);
            } catch{
                return this.adminCurrency() + ' ' + n.toFixed(2);
            }
        },

        formatDate(dateStr) {
            if (!dateStr) return '—';
            try {
                return new Intl.DateTimeFormat(this.adminLocale(), {
                    day: '2-digit', month: 'short', year: 'numeric',
                }).format(new Date(dateStr));
            } catch{
                return String(dateStr);
            }
        },
    },
});
